import {memo, useCallback} from 'react';
import styled from 'styled-components';
import {Palette} from 'hubbl-shared';
import {useAuth} from '../../store/AuthProvider';
import {useNavigate} from 'react-router-dom';
import {RootRoute} from '../../routes/RootRoute';
import {TextHeading6} from '../text/TextHeading6';
import {TextHeading8} from '../text/TextHeading8';
import {TextHeading7} from '../text/TextHeading7';
import {TextHeading9} from '../text/TextHeading9';
import {TextHeading10} from '../text/TextHeading10';
import {useI18n} from '../../hooks';

interface AdminViewToggleProps {
  className?: string;
}

export const AdminViewToggle = memo(({className}: AdminViewToggleProps) => {
  const {isAdmin, isAdminView, setIsAdminView} = useAuth();
  const navigate = useNavigate();
  const t = useI18n();

  const handleOptionClick = useCallback(
    (isAdminOption: boolean) => {
      if (!isAdmin) return;

      if (isAdminOption !== isAdminView) {
        setIsAdminView(isAdminOption);

        // Navigate to appropriate dashboard based on new view
        if (isAdminOption) {
          navigate(RootRoute.AuthAdminHome);
        } else {
          navigate(RootRoute.EmployeeHome);
        }
      }
    },
    [isAdmin, isAdminView, setIsAdminView, navigate],
  );

  // Only show for admins
  if (!isAdmin) {
    return null;
  }

  return (
    <ToggleContainer className={className}>
      <TextHeading8 color={Palette.almostBlack500} text={t.navigation.viewingAs} />

      <ToggleButton>
        <ToggleOption
          $isActive={isAdminView}
          onClick={() => handleOptionClick(true)}
          disabled={!isAdmin}>
          {isAdminView ? (
            <TextHeading9 color={Palette.almostBlack} text={t.navigation.admin} />
          ) : (
            <TextHeading10 color={Palette.almostBlack500} text={t.navigation.admin} />
          )}
        </ToggleOption>
        <ToggleOption
          $isActive={!isAdminView}
          onClick={() => handleOptionClick(false)}
          disabled={!isAdmin}>
          {!isAdminView ? (
            <TextHeading9 color={Palette.almostBlack} text={t.navigation.employee} />
          ) : (
            <TextHeading10 color={Palette.almostBlack500} text={t.navigation.employee} />
          )}
        </ToggleOption>
      </ToggleButton>
    </ToggleContainer>
  );
});

const ToggleContainer = styled.div`
  display: flex;
  align-items: center;

  margin-right: 16px;
  gap: 8px;
`;

const ToggleButton = styled.div`
  display: flex;
  background: ${Palette.almostBlackTransparent5};
  border: 1px solid ${Palette.almostBlackTransparent5};
  border-radius: 10px;
  overflow: hidden;
  position: relative;
`;

const ToggleOption = styled.button<{$isActive: boolean}>`
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props =>
    props.$isActive
      ? `linear-gradient(135deg, ${Palette.gradient1Color1} 0%, ${Palette.gradient1Color2} 100%)`
      : 'transparent'};
  position: relative;
  z-index: 1;

  &:hover {
    background: ${props =>
      props.$isActive
        ? `linear-gradient(135deg, ${Palette.gradient1Color1}80 0%, ${Palette.gradient1Color2}80 100%)`
        : Palette.almostBlackTransparent10};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  @media (max-width: 768px) {
    padding: 6px 12px;
    font-size: 11px;
  }
`;
